import React from 'react';
import { Container, Row, Col, Accordion } from 'react-bootstrap';
import './Safety.css';

function Safety() {
  const algorithms = [
    {
      name: 'Bühlmann ZH-L16C',
      description: 'The gold standard 16-compartment tissue model with gradient factors, providing precise decompression calculations trusted by professionals worldwide',
      features: [
        '16 tissue compartments with half-times from 4 to 635 minutes',
        'Gradient factors (GF Low/High) for adjustable conservatism',
        'Real-time M-value calculations for precise NDL tracking',
        'Helium and nitrogen tracking for technical diving',
        'Industry standard used by leading dive computer manufacturers',
        'Proven safe through decades of scientific research',
        'Supports repetitive diving with accurate surface intervals',
        'Compatible with altitude diving adjustments'
      ],
      icon: 'fas fa-chart-line',
      color: '#4a90e2'
    }
  ];

  const safetyFeatures = [
    {
      title: 'What safety features does ZDIVE include?',
      content: `ZDIVE includes multiple safety layers: configurable safety stops (3-6m @ 3-7 minutes), 
        ascent rate monitoring with visual and haptic warnings, 6 adjustable conservatism levels, 
        real-time NDL tracking, automatic no-fly time calculations, altitude restriction warnings, 
        depth-limit warnings approaching the 40 m rated maximum, and a remote safety-update system 
        that notifies you immediately when a safety-critical update is available.`
    },
    {
      title: 'How accurate are the decompression calculations?',
      content: `Our decompression engine implements the industry-standard Bühlmann ZH-L16C model with gradient
        factors, tracking 16 tissue compartments with precise half-times and M-values — the same algorithm family
        used by leading certified dive computers. Every release is validated against independently computed
        reference tables, and the dive computer UI warns you when approaching the 40 m rated depth limit.`
    },
    {
      title: 'Can I rely on ZDIVE as my primary dive computer?',
      content: `ZDIVE is a dive planning, logging, and monitoring instrument — not a replacement for a certified
        primary dive computer, proper training, or buddy procedures. ALWAYS dive with a dedicated, certified dive
        computer and follow its more conservative reading whenever the two disagree. Never let any single
        instrument be your only source of decompression information, and never exceed your training limits.`
    },
    {
      title: 'How does altitude diving work?',
      content: `ZDIVE includes altitude calibration that compensates surface pressure for accurate depth readings 
        at altitude. The app automatically adjusts decompression calculations and provides maximum altitude 
        restrictions based on your tissue loading.`
    },
    {
      title: 'What happens if my watch battery dies during a dive?',
      content: `ZDIVE saves dive data continuously during the dive. If the battery dies, your partial dive data 
        is preserved and will sync when the watch restarts. Always ensure adequate battery before diving and 
        consider the Apple Watch Ultra for extended battery life.`
    }
  ];

  return (
    <section id="safety" className="section-padding bg-alt">
      <Container>
        <div className="text-center mb-5" data-aos="fade-up">
          <h2 className="section-title">Safety & Algorithms</h2>
          <p className="section-subtitle">
            Professional-grade decompression models you can trust
          </p>
        </div>

        <Row className="mb-5 justify-content-center">
          {algorithms.map((algo, index) => (
            <Col lg={10} className="mb-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
              <div className="algorithm-card">
                <div className="d-flex align-items-center mb-3">
                  <div className="algo-icon-container" style={{ background: `linear-gradient(135deg, ${algo.color}dd, ${algo.color}88)` }}>
                    <i className={algo.icon + " algo-icon"}></i>
                  </div>
                  <h4 className="algo-title">{algo.name}</h4>
                </div>
                <p className="algo-description">{algo.description}</p>
                <ul className="algo-features-list">
                  {algo.features.map((feature, idx) => (
                    <li key={idx} className="algo-feature-item">
                      <i className="fas fa-check-circle algo-feature-icon" style={{ color: algo.color }}></i>
                      <span className="algo-feature-text">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          ))}
        </Row>

        <div className="conservatism-card" data-aos="fade-up">
          <Row className="align-items-center">
            <Col lg={6}>
              <h3 className="conservatism-title">
                <i className="fas fa-user-shield" style={{ marginRight: '10px', color: '#4CAF50' }}></i>
                Configurable Conservatism
              </h3>
              <p className="conservatism-description">
                Adjust safety margins based on your experience level, physical condition, and diving environment.
                Six conservatism levels ensure safe diving for everyone from beginners to experienced divers.
              </p>
              <Row>
                {[1, 2, 3, 4, 5, 6].map(level => (
                  <Col xs={4} key={level} className="mb-3">
                    <div className={`conservatism-level ${level <= 2 ? 'level-liberal' : level <= 4 ? 'level-moderate' : 'level-conservative'}`}>
                      <strong className="level-number">Level {level}</strong>
                      <div className="level-label">
                        {level <= 2 ? 'Liberal' : level <= 4 ? 'Moderate' : 'Conservative'}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
            <Col lg={6}>
              <div className="safety-monitoring">
                <h4 className="monitoring-title">Real-time Safety Monitoring</h4>
                <div className="monitoring-item">
                  <div className="monitoring-label">
                    <span>Ascent Rate</span>
                    <span>9 m/min</span>
                  </div>
                  <div className="monitoring-progress-bg">
                    <div className="monitoring-progress-bar progress-safe" style={{ width: '60%' }}></div>
                  </div>
                </div>
                <div className="monitoring-item">
                  <div className="monitoring-label">
                    <span>Tissue Loading</span>
                    <span>45%</span>
                  </div>
                  <div className="monitoring-progress-bg">
                    <div className="monitoring-progress-bar progress-warning" style={{ width: '45%' }}></div>
                  </div>
                </div>
                <div className="monitoring-item">
                  <div className="monitoring-label">
                    <span>No-Fly Time</span>
                    <span>18:24:00</span>
                  </div>
                  <div className="monitoring-progress-bg">
                    <div className="monitoring-progress-bar progress-danger" style={{ width: '75%' }}></div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        <div data-aos="fade-up">
          <h3 className="faq-title">Frequently Asked Questions</h3>
          <Accordion data-bs-auto-close="outside" data-bs-interval="false">
            {safetyFeatures.map((item, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={index}
                className="accordion-item"
                data-form-type="other"
                autoComplete="off"
              >
                <Accordion.Header>
                  <strong>{item.title}</strong>
                </Accordion.Header>
                <Accordion.Body>
                  {item.content}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      </Container>
    </section>
  );
}

export default Safety;